/**
 * Administração → Portal de pagamento (`PG-01`, ADR-081): o portal de cada concessão.
 *
 * O administrador escolhe o **endereço** (`/pagar/<endereço>`, único na instalação), o **nome**
 * que o usuário vê, o **domínio** próprio da concessão (guardado aqui; apontá-lo para o portal é
 * o DNS e o proxy da máquina do cockpit), o **contato** do atendimento e a **apresentação** que
 * abre a página — e liga ou desliga o portal. Ao lado, o que o portal tem agora: as passagens em
 * aberto e as consultas da semana. E o **recebimento** (`PG-02`, ADR-090; `PG-03`, ADR-091): o
 * provedor, a chave e o nome que o QR mostra, a credencial do provedor (guardada no cofre, nunca
 * mostrada), o segredo do aviso de pagamento — que aparece uma vez —, os endereços do aviso e os
 * **meios** que o portal oferece (PIX, boleto, cartão na página do provedor; ao menos um); e os
 * pagamentos recentes, com o meio, o vencimento do boleto, o que ficou para devolver e os estornos
 * e as contestações do cartão. E o **atraso** (`PG-06`, ADR-093): a multa e o juros que a concessão
 * cobra de quem paga depois do prazo — em percentual na tela, em pontos-base na API, zerados até a
 * concessão decidir. Toda gravação vai para a trilha administrativa.
 */
import { useCallback, useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  CobrancaNaTela,
  MeioDePagamento,
  PagamentosDaConcessao,
  PortalDaConcessao,
  PortalNovo,
  RecebedorNaTela,
  RecebedorNovo,
  SituacaoDaCobranca,
} from "../api/tipos";
import {
  Badge,
  Botao,
  Estado,
  Linha,
  Painel,
  type Significado,
} from "../componentes/base";
import { dataEHora, numeroLongo, reais } from "../formato";
import {
  JUROS_MAXIMOS_AO_MES_BP,
  MULTA_MAXIMA_BP,
  bpEmPercentual,
  percentualEmBp,
} from "./consectarios";

const CAMINHO = "/pagamento/portais";
const RECEBEDORES = "/pagamento/recebedores";

const CAMPO =
  "min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[16px] text-tinta md:text-[11.5px]";

export function PortalDePagamento() {
  const [portais, definirPortais] = useState<PortalDaConcessao[] | null>(null);
  const [recebedores, definirRecebedores] = useState<RecebedorNaTela[]>([]);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);

  const recarregar = useCallback(async () => {
    try {
      const [lidos, deQuem] = await Promise.all([
        api<PortalDaConcessao[]>(CAMINHO),
        api<RecebedorNaTela[]>(RECEBEDORES),
      ]);
      definirPortais(lidos);
      definirRecebedores(deQuem);
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  if (erro)
    return (
      <Estado
        tipo={
          erro.semLicenca
            ? "sem-licenca"
            : erro.status === 403
              ? "bloqueado"
              : "erro"
        }
        titulo={
          erro.semLicenca
            ? "Esta organização não licenciou o portal de pagamento."
            : erro.status === 403
              ? "Esta área é do administrador da organização."
              : "Não foi possível ler os portais."
        }
        detalhe={erro.message}
      />
    );
  if (!portais) return <Estado tipo="carregando" titulo="Lendo os portais…" />;
  if (!portais.length)
    return <Estado tipo="vazio" titulo="Nenhuma concessão no seu recorte." />;

  return (
    <div className="grid gap-3">
      <p className="max-w-prose text-[11.5px] text-tinta-3">
        O portal é a página do público: quem passou sem TAG consulta pela placa
        quantas passagens estão em aberto e quanto somam e, com o RENAVAM, por
        onde e quando passou. Ele só mostra a passagem que já foi ao registro
        nacional, e só da concessão dele. Com o recebimento ligado, a pessoa
        paga ali mesmo tudo o que deve no prazo, pelos meios marcados — PIX,
        boleto ou cartão, este na página do provedor —; sem ele, a página diz
        que o pagamento não é por ali e mostra o contato do atendimento.
      </p>
      {portais.map((p) => (
        <div key={p.concessao_id} className="grid gap-3">
          <DaConcessao portal={p} aoGravar={recarregar} />
          <Recebimento
            recebedor={recebedores.find(
              (r) => r.concessao_id === p.concessao_id,
            )}
            concessaoId={p.concessao_id}
            concessaoNome={p.concessao_nome}
            aoGravar={recarregar}
          />
        </div>
      ))}
    </div>
  );
}

function DaConcessao({
  portal,
  aoGravar,
}: {
  portal: PortalDaConcessao;
  aoGravar: () => Promise<void>;
}) {
  const [dados, definirDados] = useState({
    codigo: portal.codigo ?? "",
    nome_publico: portal.nome_publico ?? portal.concessao_nome,
    dominio: portal.dominio ?? "",
    contato: portal.contato ?? "",
    apresentacao: portal.apresentacao ?? "",
    ativo: portal.ativo,
  });
  const [gravando, definirGravando] = useState(false);
  const [mensagem, definirMensagem] = useState<{
    erro: boolean;
    texto: string;
  } | null>(null);

  const mudar = (campo: keyof typeof dados) => (valor: string | boolean) =>
    definirDados((d) => ({ ...d, [campo]: valor }));

  async function gravar() {
    definirGravando(true);
    definirMensagem(null);
    const corpo: PortalNovo = {
      codigo: dados.codigo,
      nome_publico: dados.nome_publico,
      dominio: dados.dominio || null,
      contato: dados.contato || null,
      apresentacao: dados.apresentacao || null,
      ativo: dados.ativo,
    };
    try {
      await api(`${CAMINHO}/${portal.concessao_id}`, { method: "PUT", corpo });
      definirMensagem({ erro: false, texto: "Portal gravado." });
      await aoGravar();
    } catch (falha) {
      definirMensagem({
        erro: true,
        texto:
          falha instanceof ErroDaApi
            ? falha.status === 422
              ? "Confira o endereço (minúsculas, números e hífen), o nome (3 a 80 letras) e o domínio (só o domínio, como pagar.suaconcessao.com.br)."
              : falha.message
            : "Não foi possível gravar.",
      });
    } finally {
      definirGravando(false);
    }
  }

  return (
    <Painel
      titulo={portal.concessao_nome}
      acoes={
        <Badge cor={portal.ativo ? "verde" : "cinza"}>
          {portal.codigo
            ? portal.ativo
              ? "no ar"
              : "desligado"
            : "não configurado"}
        </Badge>
      }
    >
      <div className="grid gap-3 md:grid-cols-[1fr_260px]">
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void gravar();
          }}
        >
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Endereço do portal
            <span className="flex items-center gap-1">
              <span className="mono text-tinta-3">/pagar/</span>
              <input
                aria-label="Endereço do portal"
                className={`${CAMPO} mono flex-1`}
                value={dados.codigo}
                onChange={(e) => mudar("codigo")(e.target.value.toLowerCase())}
                placeholder="rota-da-concessao"
                autoComplete="off"
                required
              />
            </span>
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Nome que o usuário vê
            <input
              className={CAMPO}
              value={dados.nome_publico}
              onChange={(e) => mudar("nome_publico")(e.target.value)}
              maxLength={80}
              required
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Domínio próprio (opcional)
            <input
              className={`${CAMPO} mono`}
              value={dados.dominio}
              onChange={(e) => mudar("dominio")(e.target.value.toLowerCase())}
              placeholder="pagar.suaconcessao.com.br"
              autoComplete="off"
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Contato do atendimento
            <input
              className={CAMPO}
              value={dados.contato}
              onChange={(e) => mudar("contato")(e.target.value)}
              maxLength={200}
              placeholder="0800 000 0000 · atendimento@suaconcessao.com.br"
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Apresentação (abre a página)
            <textarea
              className={`${CAMPO} min-h-16 py-1.5`}
              value={dados.apresentacao}
              onChange={(e) => mudar("apresentacao")(e.target.value)}
              maxLength={600}
            />
          </label>
          <label className="flex items-center gap-2 text-[11.5px] text-tinta-2">
            <input
              type="checkbox"
              checked={dados.ativo}
              onChange={(e) => mudar("ativo")(e.target.checked)}
            />
            Portal no ar
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Botao papel="primaria" type="submit" disabled={gravando}>
              {gravando ? "Gravando…" : "Gravar"}
            </Botao>
            {mensagem && (
              <span
                role={mensagem.erro ? "alert" : "status"}
                className={`text-[11.5px] ${mensagem.erro ? "text-vermelho" : "text-verde"}`}
              >
                {mensagem.texto}
              </span>
            )}
          </div>
        </form>
        <div className="grid content-start gap-1.5">
          <Linha rotulo="Passagens em aberto">
            {numeroLongo(portal.em_aberto)}
          </Linha>
          <Linha rotulo="Somando">{reais(portal.em_aberto_centavos)}</Linha>
          <Linha rotulo="Consultas na semana">
            {numeroLongo(portal.consultas_na_semana)}
          </Linha>
          {portal.caminho && portal.ativo && (
            <a
              className="text-[11.5px] text-azul-claro underline"
              href={portal.caminho}
              target="_blank"
              rel="noreferrer"
            >
              Abrir o portal
            </a>
          )}
          {portal.dominio && (
            <p className="text-[11px] text-tinta-3">
              O domínio próprio passa a abrir o portal quando o DNS dele apontar
              para a máquina do cockpit e o proxy o conhecer.
            </p>
          )}
        </div>
      </div>
    </Painel>
  );
}

const SITUACAO_DA_COBRANCA: Record<SituacaoDaCobranca, Significado> = {
  CRIANDO: "cinza",
  ABERTA: "azul",
  PAGA: "verde",
  EXPIRADA: "cinza",
  FALHOU: "vermelho",
  CANCELADA: "cinza",
};

const MEIOS: { meio: MeioDePagamento; rotulo: string }[] = [
  { meio: "PIX", rotulo: "PIX" },
  { meio: "BOLETO", rotulo: "Boleto" },
  { meio: "CARTAO", rotulo: "Cartão (na página do provedor)" },
];

function Recebimento({
  recebedor,
  concessaoId,
  concessaoNome,
  aoGravar,
}: {
  recebedor: RecebedorNaTela | undefined;
  concessaoId: string;
  concessaoNome: string;
  aoGravar: () => Promise<void>;
}) {
  const [dados, definirDados] = useState({
    chave_pix: recebedor?.chave_pix ?? "",
    nome_do_recebedor:
      recebedor?.nome_do_recebedor ?? concessaoNome.slice(0, 25),
    credencial: "",
    ativo: recebedor?.ativo ?? false,
  });
  const [meios, definirMeios] = useState<MeioDePagamento[]>(
    recebedor?.meios.length ? recebedor.meios : ["PIX"],
  );
  // O atraso (`PG-06`): o que a concessão cobra além da tarifa de quem paga depois do prazo, em
  // percentual na tela e em pontos-base na API. Zero até a concessão decidir.
  const [multa, definirMulta] = useState(
    bpEmPercentual(recebedor?.multa_bp ?? 0),
  );
  const [juros, definirJuros] = useState(
    bpEmPercentual(recebedor?.juros_ao_mes_bp ?? 0),
  );
  const multaBp = percentualEmBp(multa);
  const jurosBp = percentualEmBp(juros);
  const atrasoValido =
    multaBp !== null &&
    multaBp <= MULTA_MAXIMA_BP &&
    jurosBp !== null &&
    jurosBp <= JUROS_MAXIMOS_AO_MES_BP;
  const [gravando, definirGravando] = useState(false);
  const [mensagem, definirMensagem] = useState<{
    erro: boolean;
    texto: string;
  } | null>(null);
  const [segredo, definirSegredo] = useState<string | null>(null);
  const [pagamentos, definirPagamentos] =
    useState<PagamentosDaConcessao | null>(null);

  const mudar = (campo: keyof typeof dados) => (valor: string | boolean) =>
    definirDados((d) => ({ ...d, [campo]: valor }));

  async function enviar(fazer: () => Promise<RecebedorNaTela>, feito: string) {
    definirGravando(true);
    definirMensagem(null);
    try {
      const gravado = await fazer();
      definirSegredo(gravado.segredo_novo ?? null);
      definirDados((d) => ({ ...d, credencial: "" }));
      definirMensagem({ erro: false, texto: feito });
      await aoGravar();
    } catch (falha) {
      definirMensagem({
        erro: true,
        texto:
          falha instanceof ErroDaApi
            ? falha.status === 422
              ? "Confira a chave PIX (até 77 caracteres) e o nome que o QR mostra (até 25)."
              : falha.message
            : "Não foi possível gravar.",
      });
    } finally {
      definirGravando(false);
    }
  }

  function gravar() {
    if (multaBp === null || jurosBp === null) return Promise.resolve();
    const corpo: RecebedorNovo = {
      provedor: "EMULADOR",
      chave_pix: dados.chave_pix,
      nome_do_recebedor: dados.nome_do_recebedor,
      credencial: dados.credencial || null,
      ativo: dados.ativo,
      meios: MEIOS.map((m) => m.meio).filter((m) => meios.includes(m)),
      multa_bp: multaBp,
      juros_ao_mes_bp: jurosBp,
    };
    return enviar(
      () =>
        api<RecebedorNaTela>(`${RECEBEDORES}/${concessaoId}`, {
          method: "PUT",
          corpo,
        }),
      "Recebimento gravado.",
    );
  }

  function gerarSegredo() {
    return enviar(
      () =>
        api<RecebedorNaTela>(`${RECEBEDORES}/${concessaoId}/segredo`, {
          method: "POST",
        }),
      "Segredo novo gerado: o anterior parou de valer.",
    );
  }

  async function verPagamentos() {
    try {
      definirPagamentos(
        await api<PagamentosDaConcessao>(
          `/pagamento/cobrancas?concessao_id=${encodeURIComponent(concessaoId)}`,
        ),
      );
    } catch (falha) {
      definirMensagem({
        erro: true,
        texto:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível ler os pagamentos.",
      });
    }
  }

  const endereco = recebedor
    ? `${window.location.origin}${recebedor.caminho_do_webhook}`
    : "";

  return (
    <Painel
      titulo={`Recebimento — ${concessaoNome}`}
      acoes={
        <span className="flex flex-wrap gap-1">
          {recebedor?.simulado && <Badge cor="amarelo">emulador</Badge>}
          <Badge cor={recebedor?.ativo ? "verde" : "cinza"}>
            {recebedor?.configurado
              ? recebedor.ativo
                ? "recebendo"
                : "desligado"
              : "não configurado"}
          </Badge>
        </span>
      }
    >
      <div className="grid gap-3 md:grid-cols-[1fr_260px]">
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void gravar();
          }}
        >
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Provedor
            <select className={CAMPO} value="EMULADOR" disabled>
              <option value="EMULADOR">
                Emulador (demonstração: não cobra ninguém)
              </option>
            </select>
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Chave PIX da concessão
            <input
              className={`${CAMPO} mono`}
              value={dados.chave_pix}
              onChange={(e) => mudar("chave_pix")(e.target.value)}
              maxLength={77}
              autoComplete="off"
              required
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Nome que o QR mostra
            <input
              className={CAMPO}
              value={dados.nome_do_recebedor}
              onChange={(e) => mudar("nome_do_recebedor")(e.target.value)}
              maxLength={25}
              required
            />
          </label>
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Credencial do provedor
            <input
              type="password"
              className={`${CAMPO} mono`}
              value={dados.credencial}
              onChange={(e) => mudar("credencial")(e.target.value)}
              placeholder={
                recebedor?.credencial_guardada
                  ? "guardada — deixe vazio para manter"
                  : "a que o provedor deu"
              }
              autoComplete="new-password"
            />
          </label>
          <label className="flex items-center gap-2 text-[11.5px] text-tinta-2">
            <input
              type="checkbox"
              checked={dados.ativo}
              onChange={(e) => mudar("ativo")(e.target.checked)}
            />
            Receber pelo portal
          </label>
          <fieldset className="grid gap-1 text-[11.5px] text-tinta-2">
            <legend className="text-[11px] text-tinta-3">Os meios</legend>
            {MEIOS.map(({ meio, rotulo }) => (
              <label key={meio} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={meios.includes(meio)}
                  // O portal recebe por ao menos um meio: o último não se desmarca.
                  disabled={meios.length === 1 && meios.includes(meio)}
                  onChange={(e) =>
                    definirMeios((atuais) =>
                      e.target.checked
                        ? [...atuais, meio]
                        : atuais.filter((m) => m !== meio),
                    )
                  }
                />
                {rotulo}
              </label>
            ))}
          </fieldset>
          <fieldset className="grid gap-1.5 text-[11.5px] text-tinta-2">
            <legend className="text-[11px] text-tinta-3">
              Quem paga depois do prazo
            </legend>
            <p className="text-tinta-3">
              Paga a tarifa e, se a concessão cobra, a multa e o juros do
              atraso. O contrato não fixa os percentuais: começam em zero, e o
              que você preencher vale para os pagamentos gerados daqui em
              diante.
            </p>
            <div className="flex flex-wrap gap-3">
              <label className="grid gap-0.5">
                Multa, uma vez (% da tarifa, até{" "}
                {bpEmPercentual(MULTA_MAXIMA_BP)}%)
                <input
                  className="w-32 rounded-cartao border border-linha bg-painel-2 px-2 py-1 text-tinta tabular-nums"
                  inputMode="decimal"
                  value={multa}
                  onChange={(e) => definirMulta(e.target.value.slice(0, 6))}
                  aria-invalid={multaBp === null || multaBp > MULTA_MAXIMA_BP}
                />
              </label>
              <label className="grid gap-0.5">
                Juros (% ao mês, até {bpEmPercentual(JUROS_MAXIMOS_AO_MES_BP)}%)
                <input
                  className="w-32 rounded-cartao border border-linha bg-painel-2 px-2 py-1 text-tinta tabular-nums"
                  inputMode="decimal"
                  value={juros}
                  onChange={(e) => definirJuros(e.target.value.slice(0, 6))}
                  aria-invalid={
                    jurosBp === null || jurosBp > JUROS_MAXIMOS_AO_MES_BP
                  }
                />
              </label>
            </div>
            {!atrasoValido && (
              <p role="alert" className="text-vermelho">
                Use um percentual de até duas casas, dentro do teto (multa até{" "}
                {bpEmPercentual(MULTA_MAXIMA_BP)}%, juros até{" "}
                {bpEmPercentual(JUROS_MAXIMOS_AO_MES_BP)}% ao mês).
              </p>
            )}
          </fieldset>
          <div className="flex flex-wrap items-center gap-2">
            <Botao
              papel="primaria"
              type="submit"
              disabled={gravando || !atrasoValido}
            >
              {gravando ? "Gravando…" : "Gravar"}
            </Botao>
            {recebedor?.configurado && (
              <Botao
                type="button"
                disabled={gravando}
                onClick={() => void gerarSegredo()}
              >
                Gerar segredo novo
              </Botao>
            )}
            {mensagem && (
              <span
                role={mensagem.erro ? "alert" : "status"}
                className={`text-[11.5px] ${mensagem.erro ? "text-vermelho" : "text-verde"}`}
              >
                {mensagem.texto}
              </span>
            )}
          </div>
          {segredo && (
            <div
              role="status"
              className="grid gap-1 rounded-cartao border border-amarelo-borda bg-amarelo-fundo p-2 text-[11.5px] text-tinta"
            >
              <p className="font-semibold">
                O segredo do aviso de pagamento — aparece só agora:
              </p>
              <p className="mono break-all">{segredo}</p>
              <p className="text-tinta-2">
                Cadastre no provedor, junto com os endereços do aviso. O
                emulador o recebe sozinho a cada pagamento.
              </p>
            </div>
          )}
        </form>
        <div className="grid content-start gap-1.5">
          {recebedor?.configurado && (
            <>
              <p className="text-[11px] text-tinta-3">
                Endereço do aviso do PIX (webhook):
              </p>
              <p className="mono text-[11px] break-all text-tinta-2">
                {endereco}
              </p>
              <p className="text-[11px] text-tinta-3">
                Endereço do aviso do boleto e do cartão:
              </p>
              <p className="mono text-[11px] break-all text-tinta-2">
                {`${window.location.origin}${recebedor.caminho_dos_eventos}`}
              </p>
              <Linha rotulo="Credencial">
                {recebedor.credencial_guardada ? "guardada" : "nenhuma"}
              </Linha>
              <Linha rotulo="Segredo do aviso">
                {recebedor.segredo_guardado ? "guardado" : "gere um novo"}
              </Linha>
            </>
          )}
          <Botao type="button" onClick={() => void verPagamentos()}>
            {pagamentos ? "Atualizar os pagamentos" : "Ver os pagamentos"}
          </Botao>
        </div>
      </div>
      {pagamentos && <Pagamentos pagamentos={pagamentos} />}
    </Painel>
  );
}

function Pagamentos({ pagamentos }: { pagamentos: PagamentosDaConcessao }) {
  return (
    <div className="mt-3 grid gap-2 border-t border-linha pt-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-tinta-2">
        <span>
          Pagas em 30 dias:{" "}
          <b className="text-tinta">
            {numeroLongo(pagamentos.pagas_em_30_dias)}
          </b>
        </span>
        <span>
          Recebido:{" "}
          <b className="text-tinta">
            {reais(pagamentos.recebido_em_30_dias_centavos)}
          </b>
        </span>
        <span>
          Aguardando pagamento:{" "}
          <b className="text-tinta">{numeroLongo(pagamentos.abertas)}</b>
        </span>
      </div>
      {pagamentos.cobrancas.length === 0 ? (
        <p className="text-[11.5px] text-tinta-3">
          Nenhum pagamento gerado nesta concessão ainda.
        </p>
      ) : (
        <ul className="grid gap-1.5">
          {pagamentos.cobrancas.map((c) => (
            <Cobranca key={c.id} cobranca={c} rotulos={pagamentos.rotulos} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Cobranca({
  cobranca: c,
  rotulos,
}: {
  cobranca: CobrancaNaTela;
  rotulos: Record<string, string>;
}) {
  return (
    <li className="grid gap-1 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2 text-[11.5px]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="mono font-semibold text-tinta">{c.placa}</span>
        <span className="text-tinta-2 tabular-nums">
          {c.quantidade === 1 ? "1 passagem" : `${c.quantidade} passagens`},{" "}
          {reais(c.valor_centavos)}
        </span>
        <Badge cor="cinza">{c.meio_rotulo}</Badge>
        <Badge cor={SITUACAO_DA_COBRANCA[c.situacao]}>
          {c.situacao_rotulo}
        </Badge>
        {c.simulado && <Badge cor="amarelo">simulado</Badge>}
        <span className="ml-auto text-tinta-3 tabular-nums">
          {/* O boleto: o banco diz o dia, e a hora gravada é o fim dele. */}
          {c.pago_no_dia
            ? `pago no dia ${c.pago_no_dia.split("-").reverse().join("/")}`
            : dataEHora(c.paga_em ?? c.criada_em)}
        </span>
      </div>
      {c.vencimento && c.situacao !== "PAGA" && (
        <p className="text-[11px] text-tinta-3 tabular-nums">
          Vence em {c.vencimento.split("-").reverse().join("/")}
        </p>
      )}
      {(c.a_devolver > 0 ||
        c.fora_do_prazo > 0 ||
        c.aguardando > 0 ||
        c.estornos > 0) && (
        <div className="flex flex-wrap gap-1.5 text-tinta-2">
          {c.a_devolver > 0 && (
            <Badge cor="vermelho">{c.a_devolver} a devolver</Badge>
          )}
          {c.fora_do_prazo > 0 && (
            <Badge cor="amarelo">{c.fora_do_prazo} pagas depois do prazo</Badge>
          )}
          {c.acrescimo_centavos > 0 && (
            <Badge cor="cinza">
              {reais(c.acrescimo_centavos)} de multa e juros
            </Badge>
          )}
          {c.aguardando > 0 && (
            <Badge cor="cinza">{c.aguardando} aguardando a baixa</Badge>
          )}
          {c.estornos > 0 && (
            <Badge cor="vermelho">
              {c.estornos === 1
                ? "1 estorno ou contestação"
                : `${c.estornos} estornos ou contestações`}
              , {reais(c.estornado_centavos)}
            </Badge>
          )}
        </div>
      )}
      {c.a_devolver > 0 && (
        <p className="text-[11px] text-tinta-3">
          A devolver: {rotulos.SEM_DEBITO}; ou {rotulos.VALOR_MENOR}.
        </p>
      )}
      {c.e2e_id && (
        <p className="mono text-[11px] break-all text-tinta-3">{c.e2e_id}</p>
      )}
    </li>
  );
}
